import React, { useEffect, useMemo, useState } from 'react';
import { FiClock, FiKey, FiPlus, FiSave, FiShield, FiTrash2, FiUser } from 'react-icons/fi';
import { useDispatch, useSelector } from 'react-redux';
import { setTimeZone } from '../store/appSlice';
import { changePassword, fetchUserProfile, updateUserProfile } from '../store/userSlice';
import { BROKER_PRESET_OPTIONS, getImportSourceLabel } from '../utils/tradeImport';
import './ProfilePage.css';

let nextClientAccessRowId = 0;

const createClientAccessRow = (clientId = '', brokerPreset = '') => ({
  rowId: `client-access-${nextClientAccessRowId++}`,
  clientId,
  brokerPreset,
});

const ProfilePage = () => {
  const dispatch = useDispatch();
  const {
    profile,
    profileStatus,
    profileError,
    updateStatus,
    updateError,
    passwordStatus,
    passwordError,
  } = useSelector((state) => state.user);
  const { timeZone } = useSelector((state) => state.app);

  const [clientAccessRows, setClientAccessRows] = useState([createClientAccessRow()]);
  const [pan, setPan] = useState('');
  const [selectedTimeZone, setSelectedTimeZone] = useState(timeZone);
  const [workspaceError, setWorkspaceError] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordFormError, setPasswordFormError] = useState('');

  useEffect(() => {
    dispatch(fetchUserProfile());
  }, [dispatch]);

  const resolvedClientAccesses = useMemo(() => {
    if (profile?.clientAccesses?.length) {
      return profile.clientAccesses;
    }

    if (profile?.clientIds?.length) {
      return profile.clientIds.map((clientId) => ({ clientId, brokerPreset: '' }));
    }

    return [];
  }, [profile]);

  useEffect(() => {
    setClientAccessRows(
      resolvedClientAccesses.length > 0
        ? resolvedClientAccesses.map((clientAccess) => createClientAccessRow(clientAccess.clientId, clientAccess.brokerPreset || ''))
        : [createClientAccessRow()],
    );
  }, [resolvedClientAccesses]);

  useEffect(() => {
    setPan(profile?.pan || '');
  }, [profile]);

  useEffect(() => {
    setSelectedTimeZone(timeZone);
  }, [timeZone]);

  useEffect(() => {
    if (passwordStatus === 'success') {
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setPasswordFormError('');
    }
  }, [passwordStatus]);

  const activeClientRows = useMemo(
    () => clientAccessRows.filter((row) => row.clientId.trim() !== ''),
    [clientAccessRows],
  );

  const configuredPresetCount = useMemo(
    () => activeClientRows.filter((row) => row.brokerPreset).length,
    [activeClientRows],
  );

  const missingPresetCount = activeClientRows.length - configuredPresetCount;

  const handleClientRowChange = (rowId, field, value) => {
    setClientAccessRows((currentRows) => currentRows.map((row) => (
      row.rowId === rowId
        ? { ...row, [field]: value }
        : row
    )));
    setWorkspaceError('');
  };

  const addClientRow = () => {
    setClientAccessRows((currentRows) => [...currentRows, createClientAccessRow()]);
    setWorkspaceError('');
  };

  const removeClientRow = (rowId) => {
    setClientAccessRows((currentRows) => {
      const nextRows = currentRows.filter((row) => row.rowId !== rowId);
      return nextRows.length > 0 ? nextRows : [createClientAccessRow()];
    });
    setWorkspaceError('');
  };

  const handleWorkspaceSubmit = (event) => {
    event.preventDefault();

    const normalizedClientAccesses = clientAccessRows
      .map((row) => ({
        clientId: row.clientId.trim(),
        brokerPreset: row.brokerPreset || null,
      }))
      .filter((row) => row.clientId !== '');

    const duplicateClientId = normalizedClientAccesses.find((row, index) => (
      normalizedClientAccesses.findIndex((item) => item.clientId === row.clientId) !== index
    ));

    if (duplicateClientId) {
      setWorkspaceError(`Client ID ${duplicateClientId.clientId} is duplicated. Each client should appear once.`);
      return;
    }

    dispatch(updateUserProfile({
      pan: pan.trim() || null,
      clientIds: normalizedClientAccesses.map((row) => row.clientId),
      clientAccesses: normalizedClientAccesses,
    }));
  };

  const handleTimeZoneSubmit = (event) => {
    event.preventDefault();
    const nextTimeZone = (selectedTimeZone || 'Asia/Kolkata').trim();
    dispatch(setTimeZone(nextTimeZone));
    window.localStorage.setItem('timeZone', nextTimeZone);
  };

  const handlePasswordSubmit = (event) => {
    event.preventDefault();

    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordFormError('Fill in the current password, new password, and confirmation.');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordFormError('New password and confirmation do not match.');
      return;
    }

    setPasswordFormError('');
    dispatch(changePassword({
      currentPassword,
      newPassword,
      confirmationPassword: confirmPassword,
    }));
  };

  return (
    <div className="profile-page">
      <section className="profile-hero">
        <div>
          <p className="profile-eyebrow">Profile Workspace</p>
          <h1>Control client access and import defaults from one place.</h1>
          <p className="financial-page-subtitle">
            Save each client once with its broker preset so the importer can reuse the correct mapping automatically.
          </p>
        </div>

        <div className="profile-hero__identity">
          <div className="profile-hero__identity-icon"><FiUser /></div>
          <div>
            <strong>{[profile?.firstname, profile?.lastname].filter(Boolean).join(' ') || 'Workspace User'}</strong>
            <span>{profile?.email || 'Profile loading...'}</span>
            <span>{profile?.role || 'User'}{profile?.pan ? ` · PAN ${profile.pan}` : ''}</span>
          </div>
        </div>
      </section>

      <section className="profile-stat-grid">
        <article className="profile-stat-card">
          <span>Accessible Clients</span>
          <strong>{activeClientRows.length}</strong>
        </article>
        <article className="profile-stat-card">
          <span>Preset Configured</span>
          <strong>{configuredPresetCount}</strong>
        </article>
        <article className="profile-stat-card">
          <span>Needs Preset</span>
          <strong>{missingPresetCount}</strong>
        </article>
        <article className="profile-stat-card profile-stat-card--accent">
          <span>Time Zone</span>
          <strong>{timeZone}</strong>
        </article>
      </section>

      <div className="profile-grid">
        <section className="profile-card profile-card--wide">
          <div className="profile-card__header">
            <div>
              <p className="profile-card__eyebrow"><FiShield /> Client Access</p>
              <h2>Client IDs and broker presets</h2>
            </div>
            <button type="button" className="financial-button financial-button--secondary" onClick={addClientRow}>
              <FiPlus /> Add client
            </button>
          </div>

          <p className="financial-page-subtitle">
            Set the client ID and optional default broker preset here. When the preset is blank, imports for that client stay editable.
          </p>

          {profileStatus === 'failed' && <p className="financial-message financial-message--error">{profileError}</p>}

          <form onSubmit={handleWorkspaceSubmit} className="profile-form-stack">
            <div className="profile-client-table">
              <div className="profile-client-table__header">Client registry</div>
              <div className="profile-client-table__rows">
                {clientAccessRows.map((row, index) => (
                  <div key={row.rowId} className="profile-client-row">
                    <div className="profile-client-row__index">{index + 1}</div>
                    <label>
                      <span>Client ID</span>
                      <input
                        type="text"
                        value={row.clientId}
                        onChange={(event) => handleClientRowChange(row.rowId, 'clientId', event.target.value)}
                        placeholder="H188864"
                      />
                    </label>
                    <label>
                      <span>Broker Preset</span>
                      <select
                        value={row.brokerPreset}
                        onChange={(event) => handleClientRowChange(row.rowId, 'brokerPreset', event.target.value)}
                      >
                        <option value="">No preset</option>
                        {BROKER_PRESET_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value}>{option.label}</option>
                        ))}
                      </select>
                    </label>
                    <button
                      type="button"
                      className="profile-icon-button"
                      onClick={() => removeClientRow(row.rowId)}
                      aria-label={`Remove client row ${index + 1}`}
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                ))}
              </div>
            </div>

            <div className="profile-preset-list">
              {activeClientRows.length > 0 ? activeClientRows.map((row) => (
                <div key={`${row.rowId}-summary`} className="profile-preset-pill">
                  <strong>{row.clientId}</strong>
                  <span>{row.brokerPreset ? getImportSourceLabel(row.brokerPreset) : 'No preset saved'}</span>
                </div>
              )) : (
                <p className="financial-message">Add a client to control access and default import source.</p>
              )}
            </div>

            <label>
              <span>PAN</span>
              <input
                type="text"
                value={pan}
                onChange={(event) => setPan(event.target.value.toUpperCase())}
                placeholder="ABCDE1234F"
                maxLength={10}
              />
            </label>

            {workspaceError && <p className="financial-message financial-message--error">{workspaceError}</p>}
            {updateStatus === 'failed' && <p className="financial-message financial-message--error">{updateError}</p>}
            {updateStatus === 'success' && <p className="financial-message financial-message--success">Profile workspace settings saved.</p>}

            <button type="submit" className="financial-button" disabled={updateStatus === 'loading'}>
              <FiSave /> {updateStatus === 'loading' ? 'Saving...' : 'Save client setup'}
            </button>
          </form>
        </section>

        <section className="profile-card">
          <div className="profile-card__header">
            <div>
              <p className="profile-card__eyebrow"><FiClock /> Preferences</p>
              <h2>Time zone</h2>
            </div>
          </div>
          <p className="financial-page-subtitle">Used locally when the app renders date and time values.</p>

          <form onSubmit={handleTimeZoneSubmit} className="profile-form-stack">
            <label>
              <span>Preferred Time Zone</span>
              <input
                type="text"
                value={selectedTimeZone}
                onChange={(event) => setSelectedTimeZone(event.target.value)}
                placeholder="Asia/Kolkata"
              />
            </label>
            <p className="financial-message">Use an IANA zone such as Asia/Kolkata or UTC.</p>
            <button type="submit" className="financial-button financial-button--secondary">Save time zone</button>
          </form>
        </section>

        <section className="profile-card">
          <div className="profile-card__header">
            <div>
              <p className="profile-card__eyebrow"><FiKey /> Security</p>
              <h2>Change password</h2>
            </div>
          </div>

          <form onSubmit={handlePasswordSubmit} className="profile-form-stack">
            <label>
              <span>Current Password</span>
              <input
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
              />
            </label>
            <label>
              <span>New Password</span>
              <input
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
              />
            </label>
            <label>
              <span>Confirm New Password</span>
              <input
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
              />
            </label>

            {passwordFormError && <p className="financial-message financial-message--error">{passwordFormError}</p>}
            {passwordStatus === 'failed' && <p className="financial-message financial-message--error">{passwordError}</p>}
            {passwordStatus === 'success' && <p className="financial-message financial-message--success">Password changed successfully.</p>}

            <button type="submit" className="financial-button financial-button--secondary" disabled={passwordStatus === 'loading'}>
              {passwordStatus === 'loading' ? 'Updating...' : 'Update password'}
            </button>
          </form>
        </section>
      </div>
    </div>
  );
};

export default ProfilePage;
